import React, { ReactNode, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import styles from './LeavePageConfirmationDialog.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import useDictionary from 'lib/hooks/useDictionary';
export interface LeavePageConfirmationDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  message?: ReactNode;
  leaveButtonText?: string;
  keepEditingButtonText?: string;
}

export default function LeavePageConfirmationDialog({
  isOpen,
  onCancel,
  onConfirm,
  title,
  message,
  leaveButtonText,
  keepEditingButtonText,
}: LeavePageConfirmationDialogProps) {
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const { getDictionaryValue } = useDictionary();

  const titleToRender =
    title ?? getDictionaryValue('Components.MyAccount.LeavePageTitle', 'Leave page?');
  const messageToRender =
    message ??
    getDictionaryValue(
      'Components.MyAccount.LeavePageDescription',
      'You have unsaved changes. Are you sure you want to leave?'
    );
  const dialogOpenedAnnouncement = getDictionaryValue(
    'Components.MyAccount.DialogOpened',
    'dialog opened.'
  );

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleLeavePage = () => {
    onConfirm();
    onCancel();
  };

  const handleKeepEditing = () => {
    onCancel();
  };

  const handleAfterOpen = () => {
    modalContentRef.current?.focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCancel}
      onAfterOpen={handleAfterOpen}
      contentRef={(node) => {
        modalContentRef.current = node;
      }}
      className={styles.modalContent}
      overlayClassName={styles.modalOverlay}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      shouldFocusAfterRender={true}
      ariaHideApp={false}
      role="dialog"
      aria={{
        labelledby: 'leave-page-dialog-title',
        describedby: 'leave-page-dialog-message',
      }}
    >
      <div className={styles.visuallyHidden} role="status" aria-live="assertive" aria-atomic="true">
        {isOpen ? dialogOpenedAnnouncement : ''}
      </div>

      <div className={styles.dialog}>
        <div className={styles.description}>
          <h2 id="leave-page-dialog-title" className={styles.title}>
            {titleToRender}
          </h2>

          <p id="leave-page-dialog-message" className={styles.body}>
            {messageToRender}
          </p>
        </div>

        <div className={styles.ctas}>
          <CtaButton
            ctaStyle="filled"
            ctaVariant="brand"
            type="button"
            className={styles.leaveButton}
            onClick={handleLeavePage}
            text={
              leaveButtonText || getDictionaryValue('Components.MyAccount.LeavePage', 'Leave page')
            }
          />

          <CtaButton
            ctaStyle="link"
            ctaVariant="brand"
            type="button"
            className={styles.keepEditingButton}
            onClick={handleKeepEditing}
            text={
              keepEditingButtonText ||
              getDictionaryValue('Components.MyAccount.Keepediting', 'Keep editing')
            }
          />
        </div>
      </div>
    </Modal>
  );
}
